// Le contrat de l'API v1 du panel (panel/api_v1.py), écrit une fois : l'interface en tire ses
// types, et `contrato.test.ts` valide contre ces schémas chaque réponse enregistrée par le banc
// Python (panel/test_api_v1.py --grabar). Un champ renommé côté serveur casse le banc, pas l'écran.
import { z } from "zod";

export const Contacto = z.object({ tipo: z.enum(["telefono", "visitante", "email"]), valor: z.string() });
export type Contacto = z.infer<typeof Contacto>;

export const Canal = z.enum(["telefono", "whatsapp", "web", "email", "sms"]);
export type Canal = z.infer<typeof Canal>;

export const ErrorApi = z.object({
  error: z.object({ codigo: z.string(), ref: z.string().optional(), params: z.record(z.string(), z.unknown()).optional() }),
});

export const Me = z.object({
  usuario: z.object({ id: z.string(), email: z.string(), nombre: z.string(), idioma: z.string() }),
  rol: z.enum(["owner", "admin", "editor", "viewer", "operator"]),
  permisos: z.array(z.string()),
  cliente: z.object({ id: z.string(), nombre: z.string(), pais: z.string(), idioma: z.string(), fuseau: z.string() }),
  clientes: z.array(z.object({ id: z.string(), nombre: z.string(), rol: z.string() })),
  asistente: z.object({ en_linea: z.boolean(), canales: z.array(z.enum(["telefono", "whatsapp", "web"])) }),
});
export type Me = z.infer<typeof Me>;

const Contadores = z.object({ hoy: z.number(), "7d": z.number(), "30d": z.number() });

export const Cita = z.object({
  id: z.string(), titulo: z.string(), inicio: z.string(), fuseau: z.string(),
  nombre: z.string(), telefono: z.string(), email: z.string(), servicio: z.string(),
  respuesta: z.string().nullable(), meet: z.string(), lien: z.string(),
  asistencia: z.string(), asistencia_cuando: z.string(),
  // Multi-site : présents seulement quand le client a des sites (panel/sedes.py). « bahia » vide = sans baie.
  sede: z.string(), bahia: z.string(),
}).partial({
  titulo: true, fuseau: true, email: true, servicio: true, respuesta: true, meet: true, lien: true, asistencia_cuando: true,
  sede: true, bahia: true,
});
export type Cita = z.infer<typeof Cita>;

// Ce que l'assistant a rapporté (06/10/2026). Les champs sans donnée sont absents : pas d'horaires,
// pas de « hors horaires » ; pas de ticket déclaré, pas de valeur ; une personne limitée à un site
// n'a que la valeur (appels et chats n'ont pas de site).
const Ticket = z.object({ monto: z.number(), moneda: z.string() });
export const Rendimiento = z.object({
  resueltas_solo: z.number().optional(), con_humano: z.number().optional(), pendientes: z.number().optional(),
  fuera_horario: z.number().optional(),
  motivos: z.array(z.object({ clave: z.string(), n: z.number() })).optional(),
  ticket: Ticket.optional(), valor: z.number().optional(),
});
export type Rendimiento = z.infer<typeof Rendimiento>;

const DiaSerie = z.object({ dia: z.string(), n: z.number(), llamadas: z.number(), mensajes: z.number(), citas: z.number() });

export const Hoy = z.object({
  serie: z.array(DiaSerie),
  // Les 30 jours d'avant, pour la courbe en pointillés et les écarts des tarjetas.
  serie_anterior: z.array(DiaSerie),
  anterior: z.object({ llamadas: Contadores, mensajes: Contadores, citas_30d: z.number().optional() }),
  canales: z.object({ telefono: z.number(), whatsapp: z.number(), web: z.number(), email: z.number() }),
  citas_hoy: z.number(),
  // Lot 2 slick : le tableau « Actividad » et la carte « Requiere seguimiento ».
  actividad: z.array(z.object({
    ref: z.string(), canal: Canal, contacto: Contacto, cuando: z.string(), motivo: z.string(), resultado: z.string(), n: z.number(),
  })),
  seguimiento: z.array(z.object({
    ref: z.string(), canal: Canal, contacto: Contacto, cuando: z.string(), texto: z.string(), escuchar: z.boolean(),
  })),
  n_pendientes: z.number(),
  sin_marcar: z.number(),
  reclamos: z.array(z.record(z.string(), z.unknown())),
  ultima_actividad: z.string(),
  arranque: z.array(z.object({ clave: z.string(), hecho: z.boolean(), url: z.string().url().optional() })),
  llamadas: Contadores,
  mensajes: Contadores,
  citas_30d: z.number(),
  proximas: z.array(Cita),
  agenda: z.string(),
  hora_local: z.string(),
  fuseau: z.string(),
  // Vue entreprise : un bloc par site, seulement avec 2 sites visibles ou plus et sans ?sede=.
  por_sede: z.array(z.object({
    id: z.string(), nombre: z.string(), citas_hoy: z.number(), pendientes: z.number(), sin_marcar: z.number(),
  })),
  rendimiento: Rendimiento,
});
export type Hoy = z.infer<typeof Hoy>;

export const ConversacionResumen = z.object({
  ref: z.string(), canal: Canal, contacto: Contacto, cuando: z.string(),
  resumen: z.string().optional(), resultado: z.string().optional(),
  n: z.number().optional(), ultimo: z.string().optional(), quien: z.string().optional(),
});
export type ConversacionResumen = z.infer<typeof ConversacionResumen>;

export const Conversaciones = z.object({ conversaciones: z.array(ConversacionResumen) });

export const Llamada = z.object({
  tipo: z.literal("llamada"), ref: z.string(), canal: z.literal("telefono"), contacto: Contacto, cuando: z.string(),
  resumen: z.string(), resultado: z.string(),
  turnos: z.array(z.object({ quien: z.enum(["asistente", "contacto"]), texto: z.string(), t: z.number().optional() })),
  // L'audio de l'appel : disponible seulement si l'appel a été enregistré ET si le rôle peut l'écouter.
  // Fichier : GET /api/v1/grabacion?ref=<ref> (Range pris en charge).
  grabacion: z.object({ disponible: z.boolean(), duracion_s: z.number() }),
});
export const Chat = z.object({
  tipo: z.literal("chat"), ref: z.string(), canal: Canal, contacto: Contacto, cuando: z.string(),
  mensajes: z.array(z.object({ quien: z.string(), texto: z.string(), cuando: z.string() })),
  modo: z.string(),
  ventana: z.object({ abierta: z.boolean(), minutos: z.number().nullable() }),
});
export const Conversacion = z.discriminatedUnion("tipo", [Llamada, Chat]);
export type Conversacion = z.infer<typeof Conversacion>;

// Les sites d'un client (GET /sedes) : horaires par jour (lun..dom, franges « HH:MM »), baies et
// techniciens. `todas` : la personne voit tous les sites du client (sinon son adhésion la limite).
const Franjas = z.array(z.tuple([z.string(), z.string()]));
export const DIAS_SEDE = ["lun", "mar", "mie", "jue", "vie", "sab", "dom"] as const;
export const Sede = z.object({
  id: z.string(), nombre: z.string(), direccion: z.string(), fuseau: z.string(), agenda: z.string(),
  horarios: z.object({ lun: Franjas, mar: Franjas, mie: Franjas, jue: Franjas, vie: Franjas, sab: Franjas, dom: Franjas }),
  bahias: z.array(z.object({ id: z.string(), nombre: z.string(), capacidad_diaria: z.number() })),
  tecnicos: z.array(z.object({ id: z.string(), nombre: z.string() })),
});
export type Sede = z.infer<typeof Sede>;
export const Sedes = z.object({ sedes: z.array(Sede), todas: z.boolean(), fuseau: z.string() });
export type Sedes = z.infer<typeof Sedes>;

// GET /citas?dia=AAAA-MM-JJ : pour ce jour, par site regardé, chaque baie et ses rendez-vous.
export const Ocupacion = z.object({
  sede: z.string(), nombre: z.string(),
  bahias: z.array(z.object({
    id: z.string(), nombre: z.string(), capacidad_diaria: z.number(), citas: z.array(z.string()), n: z.number(),
  })),
  sin_bahia: z.array(z.string()), total: z.number(), capacidad: z.number(),
});
export type Ocupacion = z.infer<typeof Ocupacion>;

export const Citas = z.object({
  estado: z.string(), citas: z.array(Cita), detalle: z.union([ErrorApi.shape.error, z.string()]).optional(),
  ocupacion: z.array(Ocupacion).optional(),
});
export type Citas = z.infer<typeof Citas>;

export const Contactos = z.object({
  contactos: z.array(z.object({
    contacto: Contacto, canales: z.array(z.string()), ultimo: z.string(), n: z.number(),
    nombre: z.string().optional(), cita: z.string().optional(),
  })),
});
export type Contactos = z.infer<typeof Contactos>;

export const ContactoDetalle = z.object({
  contacto: Contacto, fil: z.array(ConversacionResumen), callados: z.array(z.string()),
});

export const Negocio = z.union([
  z.object({
    campos: z.record(z.string(), z.string()),
    nombre_editable: z.boolean(),
    secciones: z.array(z.object({ titulo: z.string(), texto: z.string() })),
    claves: z.array(z.string()),
    tamano: z.number(),
    annonce_texte: z.string(), voix: z.string(),
    horaires: z.record(z.string(), z.unknown()), fuseau: z.string().nullable().optional(),
    // La valeur moyenne d'un rendez-vous ({} tant que le client ne l'a pas donnée).
    ticket: z.object({ monto: z.number().optional(), moneda: z.string().optional() }),
    moneda_sugerida: z.string(), monedas: z.array(z.string()),
    editable: z.boolean(),
  }),
  ErrorApi,
]);
export type Negocio = z.infer<typeof Negocio>;

export const Canales = z.object({
  chat_web: z.object({ estado: z.string(), url: z.string(), widget: z.string() }),
  whatsapp: z.object({ estado: z.string() }),
  telefono: z.object({ estado: z.string(), numero: z.string() }),
  agenda: z.object({ estado: z.string(), id: z.string() }),
});
export type Canales = z.infer<typeof Canales>;

export const Automatizaciones = z.object({
  recetas: z.array(z.object({
    id: z.string(), estado: z.string(), plan: z.string(), canales: z.array(z.string()),
    config: z.record(z.string(), z.unknown()),
  })),
  correo: z.string(),
  whatsapp: z.boolean(),
});
export type Automatizaciones = z.infer<typeof Automatizaciones>;

export const Sesiones = z.object({
  sesiones: z.array(z.object({
    id: z.string(), actual: z.boolean(), cliente: z.string(), creada: z.number(), ultimo: z.number(),
    expira: z.number(), agente: z.string(),
  })),
});
export type Sesiones = z.infer<typeof Sesiones>;

export const Equipo = z.object({
  equipo: z.array(z.object({
    id: z.string(), email: z.string(), nombre: z.string(), rol: z.enum(["owner", "admin", "editor", "viewer"]),
    sedes: z.array(z.string()), desde: z.number(), ultimo_acceso: z.number(),
  })),
});
export type Equipo = z.infer<typeof Equipo>;

export const Auditoria = z.object({
  auditoria: z.array(z.object({
    ts: z.number(), usuario: z.string(), email: z.string(), accion: z.string(), detalle: z.record(z.string(), z.unknown()),
  })),
});
export type Auditoria = z.infer<typeof Auditoria>;

export const Analisis = z.object({
  periodo: z.object({ desde: z.string(), hasta: z.string(), fuseau: z.string() }),
  totales: z.object({
    conversaciones: z.number(), llamadas: z.number(), mensajes_entrantes: z.number(), citas_agendadas: z.number(),
    servicios_ganados: z.number(), no_vino: z.number(), sin_marcar: z.number(),
  }),
  agenda: z.string(),
  por_dia: z.array(z.object({ dia: z.string(), llamadas: z.number(), mensajes: z.number(), citas: z.number() })),
  por_canal: z.object({ telefono: z.number(), whatsapp: z.number(), web: z.number(), email: z.number(), sms: z.number().default(0) }),
  por_resultado: z.record(z.string(), z.number()),
  mapa_calor: z.array(z.array(z.number()).length(24)).length(7),
  rendimiento: Rendimiento,
});
export type Analisis = z.infer<typeof Analisis>;

const Progreso = z.object({ total: z.number(), por_estado: z.record(z.string(), z.number()), hechos: z.number(), hoy: z.number() });
const CampanaBase = z.object({
  id: z.string(), cliente: z.string(), nombre: z.string(), estado: z.enum(["borrador", "en_curso", "pausada", "terminada"]),
  secuencia: z.array(z.object({ tipo: z.enum(["whatsapp", "llamada", "esperar"]), horas: z.number().optional(), plantilla: z.string().optional() })),
  reglas: z.object({ dias: z.array(z.number()), desde: z.string(), hasta: z.string(), max_por_dia: z.number(), reintentos: z.number(), espacio_horas: z.number() }),
  columnas: z.array(z.string()), creada: z.number(), creada_por: z.string(), iniciada: z.number(), terminada: z.number(),
  progreso: Progreso,
});
export const Campanas = z.object({ campanas: z.array(CampanaBase), canales: z.record(z.string(), z.boolean()) });
export type Campanas = z.infer<typeof Campanas>;
export const CampanaDetalle = CampanaBase.extend({
  destinatarios: z.array(z.object({
    id: z.number(), telefono: z.string(), nombre: z.string(), datos: z.record(z.string(), z.string()),
    estado: z.string(), paso: z.number(), intentos: z.number(), proximo: z.number(), ultimo: z.number(), detalle: z.string(),
  })),
});
export type CampanaDetalle = z.infer<typeof CampanaDetalle>;
export const AnalisisLista = z.object({
  ok: z.literal(true), columnas: z.array(z.string()), mapeo: z.record(z.string(), z.number()), validos: z.number(),
  muestra: z.array(z.object({ telefono: z.string(), nombre: z.string(), datos: z.record(z.string(), z.string()) })),
  invalidos: z.array(z.object({ fila: z.number(), valor: z.string(), motivo: z.string() })), n_invalidos: z.number(), duplicados: z.number(),
});
export type AnalisisLista = z.infer<typeof AnalisisLista>;

export const Datos = z.object({
  grabacion: z.boolean(), retencion_meses: z.number(), opciones_retencion: z.array(z.number()),
  guardado: z.object({ llamadas: z.number(), grabaciones: z.number(), megas: z.number(), desde: z.string() }),
});
export type Datos = z.infer<typeof Datos>;

// Plan : le palier, ce qu'il inclut, ce qui a été consommé. Les champs de prix sont OPTIONNELS
// parce qu'un palier sans plan commercial (`p`, le cas de toutes les fiches aujourd'hui) ne les
// envoie pas du tout : le serveur préfère l'absence à un `null`, qui finirait affiché.
export const Plan = z.object({
  palier: z.string(), moneda: z.string(), periodo: z.string(),
  usado: z.object({ conversaciones: z.number(), llamadas: z.number(), minutos: z.number() }),
  excedido: z.boolean(), corta: z.literal(false), cobro: z.literal("transferencia"),
  plan: z.string().optional(), mensual: z.number().optional(), setup: z.number().optional(),
  unidades_incluidas: z.number().optional(),
  estimacion: z.object({
    plan: z.string(), mensual: z.number(), unidades_usadas: z.number(), unidades_incluidas: z.number(),
    excedente: z.number(), total: z.number(), regla: z.string(),
    lineas: z.array(z.object({ unidad: z.string(), cantidad: z.number(), precio: z.number(), total: z.number() })),
  }).optional(),
});
export type Plan = z.infer<typeof Plan>;

export const Ok = z.object({ ok: z.boolean() }).loose();

// Route GET → schéma de la réponse réussie.
export const LECTURAS = {
  "/me": Me,
  "/hoy": Hoy,
  "/conversaciones": Conversaciones,
  "/conversacion": Conversacion,
  "/citas": Citas,
  "/contactos": Contactos,
  "/contacto": ContactoDetalle,
  "/negocio": Negocio,
  "/canales": Canales,
  "/automatizaciones": Automatizaciones,
  "/analisis": Analisis,
  "/campanas": Campanas,
  "/datos": Datos,
  "/plan": Plan,
  "/sedes": Sedes,
  "/campana": CampanaDetalle,
  "/sesiones": Sesiones,
  "/equipo": Equipo,
  "/auditoria": Auditoria,
} as const;
export type Lectura = keyof typeof LECTURAS;
